'use client';

import useWindowSize from '@/hooks/useWindowSize';
import { cn } from '@/lib/utils';
import { VillageSurrounding } from '@/services/types';
import translateFromArray from '@/utils/get-translated-object';
import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';
import pugliaSvg from '../../../public/puglia.svg';
import sicilySvg from '../../../public/sicily.svg';
import PlaneIcon from '../Icons/Plane';

const siciliyRef = {
  origin: {
    latitude: 38.81728485,
    longitude: 11.92481948,
  },
  end: {
    latitude: 36.64863391,
    longitude: 15.65467788,
  },
};

const pugliaRef = {
  origin: {
    latitude: 41.957011,
    longitude: 14.900135,
  },
  end: {
    latitude: 39.760717,
    longitude: 18.548578,
  },
};

type MarkerPositions = {
  left: number;
  top: number;
  name?: string;
  isVillage?: boolean;
};

type Location = {
  latitude: number;
  longitude: number;
};

type Settings = {
  origin: {
    latitude: number;
    longitude: number;
  };
  end: {
    latitude: number;
    longitude: number;
  };
};

type Props = {
  lang: string;
  region: string;
  airportSurroundings: VillageSurrounding[];
  villageLocation: {
    latitude: number;
    longitude: number;
  };
};

export default function RegionSurroundingsMap({
  airportSurroundings,
  region,
  villageLocation,
  lang,
}: Props) {
  const imageRef = useRef<HTMLImageElement>(null);
  const [markerPositions, setMarkerPositions] = useState<MarkerPositions[]>([]);
  const { width } = useWindowSize();
  const villaInPuglia = region == 'puglia';

  const getSettings = useCallback(() => {
    const settings: Settings = {} as Settings;

    if (region == 'sicily') {
      settings.origin = siciliyRef.origin;
      settings.end = siciliyRef.end;
    } else {
      settings.origin = pugliaRef.origin;
      settings.end = pugliaRef.end;
    }

    return settings;
  }, [region]);

  const mapCoordinatesToPosition = useCallback(
    (villaCoordinates: { latitude: number; longitude: number }) => {
      const { origin, end } = getSettings();
      const pointCoordinates = {
        top: 0,
        left: 0,
      };

      if (imageRef.current) {
        const mapOrigin = { top: 0, left: 0 };

        const mapEnd = {
          width: imageRef.current.width,
          height: imageRef.current.height,
        };
        pointCoordinates.top =
          ((villaCoordinates.latitude - origin.latitude) *
            (mapEnd.height - mapOrigin.top)) /
            (end.latitude - origin.latitude) +
          mapOrigin.top;
        pointCoordinates.left =
          ((villaCoordinates.longitude - origin.longitude) *
            (mapEnd.width - mapOrigin.left)) /
            (end.longitude - origin.longitude) +
          mapOrigin.left;
      }
      return pointCoordinates;
    },
    [getSettings],
  );

  const getMarkerPosition = useCallback(
    (location: Location) => {
      const villa = {
        ...location,
      };

      const coordinates = mapCoordinatesToPosition(villa);

      return coordinates;
    },
    [mapCoordinatesToPosition],
  );

  useEffect(() => {
    if (imageRef.current) {
      let markers: MarkerPositions[] = [];
      const villagePosition = getMarkerPosition(villageLocation);
      airportSurroundings.map(({ surrounding }) => {
        let name = '';
        const { location } = surrounding;

        const airportTranslation = translateFromArray({
          lang,
          translations: surrounding.translate,
        });

        name = airportTranslation?.title || '';

        const airportPositions = getMarkerPosition({
          latitude: location.latitude,
          longitude: location.longitude,
        });

        markers.push({ name, ...airportPositions });
      });
      markers.push({ isVillage: true, ...villagePosition });
      setMarkerPositions(markers);
    }
  }, [
    airportSurroundings,
    getMarkerPosition,
    imageRef,
    lang,
    villageLocation,
    width,
  ]);

  return (
    <div className="w-full my-auto flex-[0_0_auto] desktop:w-[41.66667%]">
      <div className="text-center md:text-left relative mt-0 ml-0 translate-y-0 desktop:translate-y-[-10%] desktop:ml-[-5%]">
        <Image
          ref={imageRef}
          className="inline-block align-middle max-w-full h-auto"
          id="sicily-surroundings-map"
          src={villaInPuglia ? pugliaSvg : sicilySvg}
          alt="Map"
        />
        {markerPositions.map(({ name, top, left, isVillage }) => (
          <div
            key={`${top}/${left}`}
            style={{ top: top, left: left }}
            className={cn(
              'absolute flex translate-x-[-7px] translate-y-[-10px] text-sm',
              {
                'w-[25px] h-[25px] bg-blue rounded-[50%] border-[5px] border-white translate-x-[-12.5px] translate-y-[-12.5px] shadow-[0_4px_8px_rgba(0,0,0,.2)]':
                  isVillage,
              },
            )}>
            {!isVillage ? <PlaneIcon className="mr-2 w-5 h-5" /> : null}
            {name ? <span className="hidden md:block">{name}</span> : null}
          </div>
        ))}
      </div>
    </div>
  );
}
